import { cva } from 'class-variance-authority'
import { forwardRef, type InputHTMLAttributes } from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import { cn } from '@/helpers/utils'

const radioVariants = cva(
	'flex h-5 w-5 flex-none cursor-pointer appearance-none items-center justify-center rounded-full border border-neutral-300 bg-neutral-white p-1 transition-shadow duration-300 ease-in-out checked:border-[6px]',
	{
		variants: {
			variant: {
				primary:
					'checked:border-neutral-600 hover:enabled:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),0_0_0_4px_var(--color-neutral-100)] disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-100 disabled:checked:cursor-not-allowed disabled:checked:border-[6px] disabled:checked:border-neutral-300',
				brand: 'checked:border-primary-600 hover:enabled:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),0_0_0_4px_var(--color-neutral-100)] disabled:cursor-not-allowed'
			}
		},
		defaultVariants: {
			variant: 'primary'
		}
	}
)

export interface RadioProps extends InputHTMLAttributes<HTMLInputElement> {
	label: string | React.ReactNode
	labelPosition?: 'left' | 'right'
	errorMessage?: string
	helpText?: string
	variant?: 'primary' | 'brand'
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
	(
		{
			className,
			label,
			labelPosition = 'right',
			variant = 'primary',
			helpText,
			...rest
		},
		ref
	) => {
		return (
			<div className={cn('flex flex-col gap-2', className)}>
				<label className='flex cursor-pointer gap-3'>
					<input
						className={cn(
							radioVariants({ variant }),
							labelPosition === 'left' ? 'order-2' : 'order-0'
						)}
						ref={ref}
						type='radio'
						{...rest}
					/>
					{typeof label === 'string' ? (
						<p className='wrap-break-word text-base text-neutral-900'>
							{label}
						</p>
					) : (
						label
					)}
				</label>
				<InputHelpText helpText={helpText} />
			</div>
		)
	}
)

export const RadioButton = forwardRef<HTMLInputElement, RadioProps>(
	(
		{
			className,
			label,
			labelPosition = 'right',
			helpText,
			variant = 'primary',
			...rest
		},
		ref
	) => {
		return (
			<label
				className={cn(
					'flex cursor-pointer gap-1.5 rounded-lg border border-neutral-300 px-4 py-2 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:border-neutral-200 has-[input:checked]:outline-2 has-[input:disabled]:outline-neutral-200',
					className
				)}
			>
				<input
					className={cn(
						'peer',
						radioVariants({ variant }),
						labelPosition === 'left' ? 'order-2' : 'order-0'
					)}
					ref={ref}
					type='radio'
					{...rest}
				/>

				<p className='wrap-break-word text-base text-neutral-900 peer-disabled:font-medium peer-disabled:text-neutral-400'>
					{label}
				</p>
			</label>
		)
	}
)

Radio.displayName = 'Radio'
